// Fetch wrappers for the practice endpoints. Mirrors auth/api.ts in shape so
// error handling looks the same across the app.

import {
  practiceAnswerResponseSchema,
  practiceQueueResponseSchema,
  type PackId,
  type PracticeAnswerRequest,
  type PracticeAnswerResponse,
  type PracticeQueueResponse,
} from '@skena/schema'

interface ApiSuccess<T> {
  data: T
}
interface ApiError {
  error: { code: string; message: string; details?: unknown }
}
type ApiResult<T> = ApiSuccess<T> | ApiError

export class PracticeApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message)
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new PracticeApiError(err.code, err.message, res.status)
  }
  return json.data
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new PracticeApiError(err.code, err.message, res.status)
  }
  return json.data
}

export const practiceApi = {
  async getQueue(packId: PackId): Promise<PracticeQueueResponse> {
    const raw = await getJson<unknown>(`/api/practice/${packId}/queue`)
    return practiceQueueResponseSchema.parse(raw)
  },
  async submitAnswer(req: PracticeAnswerRequest): Promise<PracticeAnswerResponse> {
    const raw = await postJson<unknown>('/api/practice/answer', req)
    return practiceAnswerResponseSchema.parse(raw)
  },
}
